// src/components/Testimonials/Testimonials.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import "./Testimonials.scss";
import aboutHero from "../../assets/about-hero.jpg"; // fallback avatar if none

// Prefer env; fall back to localhost for dev
const API_BASE = "https://api.ainfotech.com.np";

function chunk(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

// Simple HTML -> text (for preview safety)
function plain(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
}

export default function Testimonials() {
  const [items, setItems] = useState([]);   // normalized for UI
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Fetch published testimonials (public)
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        // server shows published by default; passing status=published is explicit
        const res = await axios.get(`${API_BASE}/api/testimonials`, {
          params: { status: "published", page: 1, limit: 50 },
          withCredentials: false,
        });

        const list = Array.isArray(res.data?.items) ? res.data.items : [];

        const normalized = list.map((t) => ({
          quote: t.title || "What our client says",
          body: plain(t.message) || "",
          name: t.person_name || "Anonymous",
          role: t.designation || "",
          avatar: t.image ? `${API_BASE}${t.image}` : aboutHero,
        }));

        if (mounted) setItems(normalized);
      } catch (e) {
        if (mounted) {
          setErr(e?.response?.data?.error || e.message || "Failed to load testimonials");
          // Optional: fallback demo data if API fails
          setItems([
            { quote: "Perfect results!", body: "Avena helped us modernize our network and CCTV stack across sites. The rollout was smooth, the team was responsive, and the documentation was top notch.", name: "Tomm Skywalker", role: "Client of Company", avatar: aboutHero },
            { quote: "Awesome!", body: "We adopted Avena’s cloud/DevOps practices and saw faster releases with better reliability. Great guidance, clear milestones, and a solid handover.", name: "Harold Perish", role: "Client of Company", avatar: aboutHero },
            { quote: "Rock-solid delivery.", body: "From sizing to deployment, Avena handled everything professionally. Our Wi-Fi 6 rollout is faster and more stable than ever.", name: "Elena Quinn", role: "IT Manager", avatar: aboutHero },
            { quote: "Highly recommended.", body: "Their ERP customization fit our workflow perfectly, and support has been responsive and friendly.", name: "Rajesh Kumar", role: "Operations Lead", avatar: aboutHero },
          ]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, []);

  // 2-up slides; CSS stacks to 1 on small screens
  const slides = useMemo(() => chunk(items, 2), [items]);

  const next = useCallback(() => {
    if (slides.length === 0) return;
    setIdx((i) => (i + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    if (slides.length === 0) return;
    setIdx((i) => (i - 1 + slides.length) % slides.length);
  }, [slides.length]);

  if (loading) {
    return (
      <section className="avena-testimonials">
        <div className="container"><div className="t-loading">Loading testimonials…</div></div>
      </section>
    );
  }

  return (
    <section className="avena-testimonials">
      <div className="container">
        <div className="row g-4 align-items-stretch">
          {/* LEFT: intro */}
          <div className="col-12 col-lg-4">
            <div className="t-intro h-100">
              <h2 className="t-eyebrow">People Say About</h2>
              <h3 className="t-title">
                Our <span>Company</span>
              </h3>
              <p className="t-copy">
                We deliver secure, reliable IT &amp; software solutions—backed by clear
                communication, strong project management, and dependable support.
              </p>
              {err && <p className="t-error" role="status">{err}</p>}
            </div>
          </div>

          {/* RIGHT: custom slider */}
          <div className="col-12 col-lg-8">
            <div className="t-slider">
              <div
                className="t-track"
                style={{ transform: `translateX(-${idx * 100}%)` }}
              >
                {slides.map((group, s) => (
                  <div className="t-slide" key={s} aria-hidden={s !== idx}>
                    <div className="row g-4">
                      {group.map((t, i) => (
                        <div className="col-12 col-md-6" key={i}>
                          <article className="t-card h-100">
                            <h5 className="t-quote">“{t.quote}”</h5>
                            <p className="t-body">{t.body}</p>
                            <div className="d-flex align-items-center gap-3 mt-3">
                              <div className="t-avatar">
                                <img src={t.avatar} alt={t.name} />
                              </div>
                              <div>
                                <div className="t-name">{t.name}</div>
                                {t.role && <div className="t-role">{t.role}</div>}
                              </div>
                            </div>
                          </article>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom arrows centered */}
              <div className="t-bottom-controls">
                <button type="button" className="t-navbtn" onClick={prev} aria-label="Previous">‹</button>
                <button type="button" className="t-navbtn" onClick={next} aria-label="Next">›</button>
              </div>
            </div>

            {/* Optional dots */}
            {/* {slides.length > 1 && (
              <div className="t-dots" role="tablist" aria-label="Testimonials slides">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    className={`t-dot ${i === idx ? "is-active" : ""}`}
                    onClick={() => setIdx(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    aria-selected={i === idx}
                    role="tab"
                  />
                ))}
              </div>
            )} */}
          </div>
        </div>
      </div>
    </section>
  );
}
